import Stack from 'components/elements/Stack'
import Link from 'next/link'
import { Body1 } from 'stories/elements/Typography/Text'
import { knowRoute } from '../routes'

export const getTopicDescription = (slug: string) => {
    switch (slug) {
        case 'cells':
            return (
                <Stack>
                    <Body1>
                        <strong>Cells</strong> are the smallest and most basic building blocks of all living organisms. A
                        living organism can be made of one singular cell like a bacteria, or many cells like animals such as
                        cats, dogs, humans. New cells can only be produced from existing cells.
                    </Body1>
                    <Body1>
                        Multiple cells of the same kind can come together to form a tissue; and these tissues come together
                        to form an organ. When several organs work together, this is called an <strong>organ system</strong>,
                        for example, the digestive system is an organ system.
                    </Body1>
                    <Body1>
                        There are different types of cells grouped under two main categories: prokaryotic and eukaryotic.
                        While prokaryotic cells are the more basic form of a cell, eukaryotic cells are more advanced and
                        complex.
                    </Body1>
                    <Body1>
                        A <strong>prokaryote</strong> is an organism that has only one cell that lacks a nucleus and any
                        other <strong>membrane-bound organelles</strong>. For example, bacteria and archaea are prokaryotes,
                        they are prokaryotic cells. The name prokaryote comes from a combination of the words “pro” and
                        “kary” which means “before” and “nucleus” respectively. These are the organisms that existed before
                        the involvement of the nucleus.
                    </Body1>
                    <Body1>
                        <strong>Eukaryotic cells</strong> have a nucleus with a double membrane that carries DNA. These cells
                        are larger compared to prokaryotic cells and have membrane bound organelles. Eukaryotic cells
                        theorized to be evolved from prokaryotic cells. Examples of eukaryotic cells include animal cells and
                        plant cells.
                    </Body1>
                    <Body1>
                        A <strong>stem cell</strong> is the cell before undergoing the transition that will determine the
                        special category of it. For example, a stem cell can turn into a cell that will serve as a building
                        block for stomach tissue. A stem cell can turn into any type of cell and contribute to a target
                        tissue and organ. As cited in Biology for AP® Courses, scientists in Cincinnati Children’s Hospital
                        Medical Centre have been developing a method to manipulate stem cells in a laboratory environment to
                        become specialized into their desired type of cells. This is a great advancement in cancer treatment
                        especially.
                    </Body1>
                    <Body1>
                        To learn more about <strong>cell biology</strong>, check out this{' '}
                        <Link
                            href={knowRoute('f98de35d-c70b-4b03-a485-e083074aac56', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            study note
                        </Link>{' '}
                        by our Knowunity user Micheala.
                    </Body1>
                    <Body1>
                        To learn more about cells with a special focus on <strong>cell cycle and division</strong>, take a
                        look at{' '}
                        <Link
                            href={knowRoute('0a006c7-460a-46b1-96ed-386628adbb9a', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            this study note
                        </Link>{' '}
                        by our Knowunity user Hannah.
                    </Body1>
                    <Body1>
                        <Link
                            href={knowRoute('23c1c3ba-ac8b-463e-ad23-3ac2fd8bc7f3', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            This flashcard set
                        </Link>{' '}
                        prepared by our Knowunity US Ambassador Miranda can help you study and memorise the important
                        scientists who made significant research on cells, components of a cell, and even{' '}
                        <strong>laboratory equipment</strong> relevant to the topic of cells in Biology.
                    </Body1>
                    <Body1>Sources:</Body1>
                    <Body1>
                        <Link
                            href={knowRoute('a630f5c0-22ae-4514-a71a-10c5433702e7', {
                                referrerScreen: 'topic-description-source',
                            })}
                            prefetch={false}
                        >
                            Cells Revision
                        </Link>
                        , study note uploaded to Knowunity by Val, 2023.
                    </Body1>
                    <Body1>
                        <Link
                            href={knowRoute('01449925-6305-4bec-93b4-ea3fd7a2c680', {
                                referrerScreen: 'topic-description-source',
                            })}
                            prefetch={false}
                        >
                            Biology Notes for the EOC and more
                        </Link>
                        , study note uploaded to Knowunity by @makani_v8606, 2023.
                    </Body1>
                    <Body1>
                        Zedalis, Julianne, and John Eggebrecht. Biology for AP® Courses. Rice University, 2018.
                        <br />
                        <a href={'https://openstax.org/details/books/biology-ap-courses'}>
                            https://openstax.org/details/books/biology-ap-courses
                        </a>
                    </Body1>
                </Stack>
            )
        case 'evolution-1':
            return (
                <Stack>
                    <Body1>
                        According to the biology{' '}
                        <Link
                            href={knowRoute('0853a253-2963-4f4d-b075-967f476db3b5', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            study notes
                        </Link>{' '}
                        of Isabela, the biological history of life can be defined by evolution, a notion heavily influenced
                        by British biologist Charles Darwin. Darwin saw a variety of adaptations in species that improved
                        their survival in diverse settings when studying the plants and animals along the coast of South
                        America. He came to the conclusion that species change as a result of environmental adaptation.
                        Modern evolutionary theory was founded on this theory, which is backed by data from fossils and{' '}
                        <strong>DNA</strong>.
                    </Body1>
                    <Body1>
                        On the other hand, Isabella underlines in her{' '}
                        <Link
                            href={knowRoute('0853a253-2963-4f4d-b075-967f476db3b5', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            notes
                        </Link>
                        , French naturalist Jean Baptiste Lamarck also observed that species changed throughout time.
                        According to Lamarck&apos;s <strong>theory of evolution</strong>, evolution is a process of
                        adaptation in which an organism acquires traits by using or not using certain body components, which
                        are subsequently passed on to their progeny. But this idea, called the{' '}
                        <strong>&quot;inheritance of acquired characteristics&quot;</strong>, does not have the same level of
                        empirical backing as Darwin&apos;s hypothesis.
                    </Body1>
                    <Body1>
                        There is ample evidence for evolution, and one important piece of that evidence is the fossil record,
                        as suggested by the study notes. It keeps the remnants of extinct species preserved in tar, ice,
                        amber, or rock layers. Evolutionary timelines are further supported by the dating of these{' '}
                        <strong>fossils</strong>, whether the dating is absolute (using radiometric techniques and natural
                        atomic decay to estimate age) or relative (determining the age based on the sequence of rock layers).
                        The foundation of evolutionary biology is firmly established by the extensive data supporting
                        Darwin&apos;s contributions, according to Pranav’s{' '}
                        <Link
                            href={knowRoute('8f966ad3-a930-4169-9131-b2552fd347df', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            study notes
                        </Link>{' '}
                        that further explain natural selection, variation, genetic variation, behavioural, structural, and
                        psychological adaptations, and endosymbiotic theory.
                    </Body1>
                </Stack>
            )
        case 'genes-1':
            return (
                <Stack>
                    <Body1>
                        A <strong>gene</strong> is a sequence of nucleotides within DNA that encodes the production of a
                        protein or <strong>RNA molecule</strong> and serves as the fundamental unit of heredity. Genes
                        control protein synthesis, supplying the instructions required to construct these molecules. Genes in
                        diverse creatures, including humans, can range in length from a few hundred to over a million bases.
                    </Body1>
                    <Body1>
                        According to the{' '}
                        <Link
                            href={knowRoute('7f5e3925-4c03-48b2-ae89-5d98942259a4', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            study notes
                        </Link>{' '}
                        of Lily. <strong>gene expression</strong> entails turning the genetic information encoded in DNA into
                        the blueprints required for protein production. This method is separated into two main stages:
                        transcription and translation. RNA polymerase, an enzyme found in the cell&apos;s nucleus,
                        transcribes DNA into messenger RNA (mRNA). This mRNA is subsequently used as a template for protein
                        synthesis during translation.
                    </Body1>
                    <Body1>
                        The <strong>nitrogenous bases</strong> (AUGC) of RNA combine with the bases (ATGC) of DNA to produce
                        mRNA when this enzyme unzips the DNA strand. The freshly created mRNA leaves the nucleus and enters
                        the cytoplasm following transcription. <strong>Ribosomes</strong> read the mRNA three codons at a
                        time during translation, the second stage of protein synthesis. Every codon designates a certain
                        amino acid (glycine is coded by the codon GGU, for instance).
                    </Body1>
                    <Body1>
                        The right <strong>amino acids</strong> are subsequently transported to the ribosome by transfer RNA
                        (tRNA), where they are joined in the sequence determined by the mRNA codons to form a{' '}
                        <strong>polypeptide chain</strong> that finally folds into a <strong>functioning protein</strong>.
                    </Body1>
                </Stack>
            )
        case 'the-carbon-cycle':
            return (
                <Stack>
                    <Body1>
                        Fun fact: Carbon as an element is the second most abundant element found in living organisms! All
                        organic molecules have Carbon, even rocks, oceans, and atmosphere!
                    </Body1>
                    <Body1>
                        According to{' '}
                        <Link
                            href={knowRoute('f6213a9d-0f45-4170-aa49-08b417d1ca4b', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            this study note
                        </Link>{' '}
                        by our Knowunity user Xiuyi, <strong>carbon cycle</strong> is the movement of carbon between abiotic
                        and biotic factors. To put simply, we can observe carbon cycle in two categories; carbon exchange
                        between living organisms and cycling of carbon through a geologic process. These cycles are known as{' '}
                        <strong>biological carbon cycle</strong> and <strong>biogeochemical carbon cycle</strong>{' '}
                        respectively.
                    </Body1>
                    <Body1>
                        Carbon moves through the planet like a traveller, it can be found in the air, water, all living
                        organisms, and even soil. Story of the carbon cycle starts at <strong>carbon dioxide (CO2)</strong>,
                        which is a gas. Plants use carbon dioxide through photosynthesis and use the energy they gain from
                        sunlight to turn carbon dioxide into food. When animals eat these plants, they consume that carbon
                        and it becomes a part of them. When plants and animals breathe out or die and decompose, the carbon
                        in them returns to nature as carbon dioxide in the air or carbon in the ground. When carbon stays
                        underground for a long time, it turns into fossil fuels like coal and oil.
                    </Body1>
                    <Body1>
                        As Bee explains in her{' '}
                        <Link
                            href={knowRoute('f0f21de1-4e43-4794-8afc-d6efb56941ce', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            study note
                        </Link>{' '}
                        , the oceans also take part in the carbon cycle. Tiny organisms like algae use carbon dioxide like
                        plants do. When they die and sink to the bottom of the ocean, the carbon in them becomes a part of
                        the sea bed.
                    </Body1>
                    <Body1>
                        According to Catalina’s{' '}
                        <Link
                            href={knowRoute('7b9317dc-2711-44f1-9a71-8758010ebd73', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            study note
                        </Link>{' '}
                        on environmental effects of the carbon cycle, burning fossil fuels releases carbon dioxide into the
                        atmosphere and when done in extreme amounts it causes <strong>global warming</strong> like we face
                        today. Carbon dioxide traps the heat and makes the planet warmer. Understanding the carbon cycle,
                        also helps us understand the <strong>environmental crisis</strong> that is climate change due to
                        global warming.
                    </Body1>
                </Stack>
            )
        case 'chemical-reactions-3':
            return (
                <Stack>
                    <Body1>
                        According to the chemistry{' '}
                        <Link
                            href={knowRoute('3d075410-e94d-4c09-9501-78fd7e609d89', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            study note
                        </Link>{' '}
                        of Kevy, when atoms rearrange to generate new compounds, a <strong>chemical reaction</strong> takes
                        place involving one or more of the original substances. The <strong>collision theory</strong>, which
                        states that particles (atoms or molecules) must collide with sufficient energy and in the right
                        orientation for a reaction to occur, is frequently used to explain this change. Even though there are
                        many collisions, not all of them cause a response.
                    </Body1>
                    <Body1>
                        Kevy also{' '}
                        <Link
                            href={knowRoute('3d075410-e94d-4c09-9501-78fd7e609d89', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            notes
                        </Link>{' '}
                        that the release of gas (<strong>effervescence</strong>), the creation of a precipitate, color
                        changes, light production, and heat or flame emission are all signs of a chemical reaction. Chemical
                        equations are symbolic depictions of these reactions that use formulas to describe the products (the
                        newly formed compounds displayed on the right of the arrow) and reactants (the starting substances on
                        the left of the reaction arrow).
                    </Body1>
                    <Body1>
                        In addition, according to{' '}
                        <Link
                            href={knowRoute('3d075410-e94d-4c09-9501-78fd7e609d89', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            Kevy
                        </Link>{' '}
                        , the equation may contain symbols for other physical states, such as solid, liquid, gas, or aqueous
                        solutions. It may also indicate <strong>modifications</strong>, such as the addition of heat or the
                        presence of a catalyst, like platinum, to speed up the reaction.
                    </Body1>
                    <Body1>
                        According to Maria’s{' '}
                        <Link
                            href={knowRoute('a95b0a93-2328-47a4-ba40-ffcc25c94fe2', { referrerScreen: 'topic-description' })}
                            prefetch={false}
                        >
                            study notes
                        </Link>{' '}
                        on chapter 8.2 of the chemistry class, there are different types of chemical reactions.
                    </Body1>
                    <Body1>
                        First, we examine synthesis or combination reactions, where two or more reactants combine to form a
                        single product, exemplified by reactions like H2 + O2 → H2O and Mg + O2 → MgO. There are no special
                        conditions required for these reactions.
                    </Body1>
                    <Body1>
                        Next, decomposition reactions are explored, where a single compound breaks down into two or more
                        products, such as H2O2 → H2O + O2 and MgCO3 → MgO + CO2.
                    </Body1>
                    <Body1>
                        {`The third type covered is single displacement or replacement reactions, where an element reacts with an ionic compound, leading to reactions like Li + Fe2O3 → Li2O + Fe and F2 + HBr —> HF + Br2, with the condition that the element must be more reactive according to the activity series of metals or the periodic table for halogens. `}
                    </Body1>
                    <Body1>
                        Lastly, double displacement reactions involve the exchange between two ionic reactants to form
                        products such as AgNO3(aq) + BaCl2(aq) → AgCl(s) + Ba(NO3)2(aq), where one of the products is
                        typically a solid, liquid, or gas.
                    </Body1>
                </Stack>
            )
        default:
            return null
    }
}
